import { type ReactNode, useMemo } from 'react';
import { DataTable } from '../../components/admin/data-table';
import { useIsSmallScreen } from '../../hooks/useIsSmallScreen';
import { MoonstarList } from '../Moonstar/MoonstarList';
import { MoonstarListActions } from '../Moonstar/MoonstarListActions';
import { type TitleBreadcrumb } from '../Moonstar/MoonstarTitle';
import { StringArrayBadgeField } from './StringArrayBadgeField';
import { createBasicUserFilters } from './UserFilters';
import { UserPictureField } from './UserPictureField';

export type UserAssignmentListProps = {
  /** The users filter, e.g. `{ roleIds: [1] }` or `{ excludeRoleIds: [1] }` (disabled users are always excluded) */
  filter: Record<string, unknown>;
  title?: TitleBreadcrumb[];
  empty: ReactNode;
  bulkActionButtons: ReactNode;
};

/**
 * The (active) users assigned, or not, to something (role users, user group members), with bulk add / remove actions.
 */
export const UserAssignmentList = ({
  filter,
  title,
  empty,
  bulkActionButtons,
}: UserAssignmentListProps) => {
  const isSmallScreen = useIsSmallScreen();
  const filters = useMemo(
    () => createBasicUserFilters({ isSmallScreen }),
    [isSmallScreen]
  );

  return (
    <MoonstarList
      title={title}
      empty={empty}
      filters={filters}
      filter={{ ...filter, userIsDisabled: false }}
      sort={{ field: 'lastName', order: 'ASC' }}
      actions={<MoonstarListActions hideCreate />}
      resource="users"
    >
      <DataTable bulkActionButtons={bulkActionButtons} rowClick={false}>
        <DataTable.Col
          source="pictureUrl"
          disableSort
          className="w-px whitespace-nowrap"
        >
          <UserPictureField />
        </DataTable.Col>
        <DataTable.Col source="internalId" label="Internal ID" />
        <DataTable.Col source="firstName" />
        <DataTable.Col source="lastName" />
        <DataTable.Col source="phoneNumber" />
        <DataTable.Col source="email" />
        <DataTable.Col source="jobTitleName" />
        <DataTable.Col source="departmentName" />
        <DataTable.Col source="regionName" />
        <DataTable.Col source="units" disableSort>
          <StringArrayBadgeField source="units" />
        </DataTable.Col>
      </DataTable>
    </MoonstarList>
  );
};
